import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SIGNAL_REFRESH_MS } from './signalRefresh';
import { useNavigate, useParams } from 'react-router-dom';
// This app has no global HelmetProvider — each page supplies its own, matching
// BuilderDocsPage / CommunityPage. A bare <Helmet> throws and blanks the whole route.
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  getDeployment,
  updateDeployment,
  getNotificationPrefs,
  saveNotificationPrefs,
  formatSignalMoney,
  formatBarTime,
  summarizeSignalTrades,
  maxDrawdownPct,
  CHANNEL_LABELS,
  EVENT_LABELS,
  followSignal,
  unfollowSignal,
  RISK_MODE_LABELS,
  RISK_MODE_HINTS,
  bucketTradesByPeriod,
  buildTimeTicks,
  createListing,
  removeListing,
  subscribeToListing,
  cancelSubscription,
} from './signalDataFeeds';
import { useConfirm } from './ConfirmDialog';
import './SignalDetailPage.css';

// Paper follow — a free user tracking someone else's signal in their own virtual account.
//
// The account is a pure re-sizing of the creator's fills, so it is authentic (real entries,
// real exits, real outcomes) while costing nothing and taking nothing from the creator. It is
// the conversion mechanism: someone who has watched a signal work in an account sized like
// their own is far more likely to pay for it than someone reading a chart.
function PaperFollowPanel({ deploymentId, follow, ledger, onChanged }) {
  const [deposit, setDeposit] = useState(follow?.deposit || 10000);
  const [riskMode, setRiskMode] = useState(follow?.riskMode || 'proportional');
  const [riskValue, setRiskValue] = useState(follow?.riskValue || 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await followSignal(deploymentId, { deposit: Number(deposit), riskMode, riskValue: Number(riskValue) });
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="signal-panel signal-follow-panel">
      <h3>{follow ? 'Your practice account' : 'Follow in practice'}</h3>

      {follow && ledger ? (
        <>
          <div className="signal-follow-summary">
            <div>
              <span className="muted-text">Balance</span>
              <strong className={ledger.netProfit >= 0 ? 'up' : 'down'}>
                ${ledger.balance.toFixed(2)}
              </strong>
            </div>
            <div>
              <span className="muted-text">Return</span>
              <strong className={ledger.returnPct >= 0 ? 'up' : 'down'}>
                {ledger.returnPct >= 0 ? '+' : ''}{ledger.returnPct.toFixed(2)}%
              </strong>
            </div>
            <div>
              <span className="muted-text">Trades</span>
              <strong>{ledger.trades}</strong>
            </div>
          </div>
          {ledger.blownAt && (
            <p className="signal-error">
              This account was wiped out on {new Date(Number(ledger.blownAt)).toLocaleDateString()}.
              Your risk setting was too large for this strategy's losing runs.
            </p>
          )}
          {ledger.trades === 0 && (
            <p className="muted-text">
              Tracking from now — your account fills in as new signals fire. Earlier trades are
              not counted, so this is genuinely your record.
            </p>
          )}
        </>
      ) : (
        <p className="muted-text">
          Track this signal in a virtual account sized like yours. Free, no MetaTrader, and it
          only counts trades from the moment you start.
        </p>
      )}

      <div className="signal-follow-form">
        <label className="signal-field">
          <span>Starting balance (USD)</span>
          <input type="number" min="100" step="100" value={deposit} onChange={(e) => setDeposit(e.target.value)} />
        </label>
        <label className="signal-field">
          <span>Position sizing</span>
          <select value={riskMode} onChange={(e) => setRiskMode(e.target.value)}>
            {Object.entries(RISK_MODE_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
          <small className="muted-text">{RISK_MODE_HINTS[riskMode]}</small>
        </label>
        {riskMode !== 'proportional' && (
          <label className="signal-field">
            <span>{riskMode === 'percent_risk' ? 'Risk per trade (%)' : 'Lot size'}</span>
            <input
              type="number"
              min="0.01"
              step={riskMode === 'percent_risk' ? '0.5' : '0.01'}
              value={riskValue}
              onChange={(e) => setRiskValue(e.target.value)}
            />
          </label>
        )}
        {error && <p className="signal-error">{error}</p>}
        <div className="inline-actions">
          <button type="button" className="primary-cta compact-button" disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : follow ? 'Update' : 'Start following'}
          </button>
          {follow && (
            <button
              type="button"
              className="ghost-button compact-button"
              onClick={async () => { await unfollowSignal(deploymentId); await onChanged(); }}
            >
              Stop
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// Signal detail — the page a notification links to.
//
// The hero is the equity curve, drawn with the SEED history and the LIVE portion visually
// separated. That split is the honest version of a track record: it makes clear at a glance
// how much is replayed history used to fill the chart and how much has actually been proven
// forward, which is exactly the distinction a subscriber needs before trusting a signal.

const NOTIFY_OPTIONS = ['trade_open', 'trade_close', 'position_update'];

// Deep link into the Builder.
//
// The query parameter is `strategy` — NOT `strategyId`. That is the name StrategyTester has
// always read, and getting it wrong silently opens an empty Builder, which is exactly how this
// shipped broken the first time.
//
// For a replay we also pass an explicit date range. Without it the Builder uses the strategy's
// SAVED range, which by definition predates every live trade, so the trade being replayed would
// not be in the tested window at all.
const DAY_MS = 86400000;

function builderLink(strategyId, trade, deployment) {
  if (!strategyId) return null;
  // liveFrom marks where the deployment began, so the chart can separate replayed history
  // from trades taken forward. Passed on every link, not just replays.
  const liveFromBarTime = deployment?.liveFromBarTime;
  const live = Number(liveFromBarTime) > 0 ? `&liveFrom=${Math.trunc(Number(liveFromBarTime))}` : '';
  if (!trade) return `/tester?strategy=${strategyId}${live}`;

  const iso = (ms) => new Date(ms).toISOString().slice(0, 10);
  // Enough history before the entry for indicators to warm up and for context to be visible.
  const from = iso(Number(trade.entryTime) - (90 * DAY_MS));

  // The end of the window decides whether the chart is LIVE. The Builder only subscribes to
  // Deriv's tick stream when the tested range includes now (dateRangeIncludesNow), so a
  // window ending in the past renders a frozen chart. A running deployment is still trading,
  // so its replay must run to today — otherwise an active signal looks dead. Only a stopped
  // deployment gets a window that closes shortly after the trade.
  const stillRunning = deployment?.status === 'running' || deployment?.status === 'paused';
  const to = stillRunning
    ? iso(Date.now())
    : iso(Math.min(Date.now(), Number(trade.exitTime || trade.entryTime) + (5 * DAY_MS)));

  return `/tester?strategy=${strategyId}&from=${from}&to=${to}${live}`;
}

// "Nice" round values for the money axis. 1/2/5 × a power of ten, so the labels read as
// 10,500 / 11,000 rather than 10,483.27 — an axis nobody can read is decoration, not a scale.
function niceScale(min, max, targetCount = 5) {
  const span = max - min;
  if (!(span > 0)) return { ticks: [min], niceMin: min - 1, niceMax: max + 1 };
  const rawStep = span / targetCount;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  // Guarded rather than a bare while: a bad step would otherwise spin forever.
  for (let value = niceMin, guard = 0; value <= niceMax + step / 2 && guard < 40; value += step, guard += 1) {
    ticks.push(Number(value.toFixed(6)));
  }
  return { ticks, niceMin, niceMax };
}

// The unit is chosen from the axis MAXIMUM and applied to every label, so a scale never reads
// "9500 / 10.0k / 10.5k" — mixing units within one axis makes it momentarily unreadable.
function makeAxisFormatter(axisMax) {
  const magnitude = Math.abs(Number(axisMax) || 0);
  if (magnitude >= 1000000) return (value) => `${(value / 1000000).toFixed(2)}M`;
  if (magnitude >= 10000) return (value) => `${(value / 1000).toFixed(1)}k`;
  return (value) => Number(value).toFixed(0);
}

// `baseline` is the balance at deployment, not the original deposit.
//
// The original deposit used to be folded into the y-range, which is fine only while the seeded
// backtest stays near it. A signal seeded from $1,000 that grew to $9,900 in replay dragged the
// axis floor to 0 and squeezed its real $226 of variation into 2.5% of the chart height — a
// dead flat line. The range now comes from the plotted series alone, and the reference line is
// an actual equity sample, so it is always inside the range by construction.
function EquityChart({ equity, liveFromBarTime, baseline, mode = 'live' }) {
  const [hover, setHover] = useState(null);

  const geometry = useMemo(() => {
    const points = (equity || [])
      .map((point) => ({
        t: Number(point.barTime),
        v: Number(point.equity),
        b: Number(point.balance),
      }))
      .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.v));
    if (points.length < 2) return null;

    const values = points.map((point) => point.v);
    const balances = points.map((point) => point.b).filter(Number.isFinite);
    // The range comes from the PLOTTED series only. Folding in an outside figure — the deposit,
    // previously — lets one number that is nowhere near the curve dictate the whole axis.
    const dataMin = Math.min(...values, ...balances);
    const dataMax = Math.max(...values, ...balances);
    // Breathing room so the curve never sits welded to the top or bottom edge. A flat series
    // has no span of its own, so it falls back to a small absolute pad rather than collapsing
    // to a zero-height range.
    const span = dataMax - dataMin;
    const pad = span > 0 ? span * 0.08 : Math.max(1, Math.abs(dataMax) * 0.001);
    // The axis is built from ROUNDED bounds, and the curve is then plotted against those same
    // bounds — otherwise the gridlines would not line up with the labels beside them.
    const { ticks: valueTicks, niceMin, niceMax } = niceScale(dataMin - pad, dataMax + pad, 5);
    const range = niceMax - niceMin || 1;

    const width = 1000;
    const height = 260;
    const x = (index) => (index / (points.length - 1)) * width;
    const y = (value) => height - ((value - niceMin) / range) * (height - 20) - 10;

    // Backtest mode has no live segment — the whole curve is the strategy's own record, so
    // there is no boundary to mark and nothing to style as "proven forward".
    const liveIndex = mode === 'backtest'
      ? -1
      : points.findIndex((point) => point.t >= Number(liveFromBarTime));
    const splitAt = mode === 'backtest'
      ? points.length - 1
      : (liveIndex < 0 ? points.length - 1 : liveIndex);

    const toPath = (slice, offset) => slice
      .map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index + offset).toFixed(1)},${y(point.v).toFixed(1)}`)
      .join(' ');

    const balancePoints = points.filter((point) => Number.isFinite(point.b));

    return {
      width,
      height,
      points,
      ticks: buildTimeTicks(equity, 5),
      // Ratios (0–1) so the labels can be positioned as HTML and stay legible — text inside a
      // preserveAspectRatio="none" SVG is stretched horizontally and looks broken.
      valueTicks: valueTicks
        .filter((value) => value >= niceMin && value <= niceMax)
        .map((value) => ({ value, ratio: y(value) / height })),
      balancePath: balancePoints.length >= 2
        ? balancePoints
          .map((point, index) => `${index === 0 ? 'M' : 'L'}${x(points.indexOf(point)).toFixed(1)},${y(point.b).toFixed(1)}`)
          .join(' ')
        : '',
      seedPath: toPath(points.slice(0, splitAt + 1), 0),
      livePath: splitAt < points.length - 1 ? toPath(points.slice(splitAt), splitAt) : '',
      // Reference line at the balance the signal went live with, so the curve above or below it
      // reads directly as live gain or loss. Clamped to the plotted range as a belt-and-braces
      // guard: a baseline outside the range would draw the line off the top or bottom edge.
      baselineY: y(Math.min(Math.max(
        Number.isFinite(Number(baseline)) ? Number(baseline) : points[0].v,
        niceMin,
      ), niceMax)),
      splitX: x(splitAt),
      splitRatio: x(splitAt) / width,
      hasSplit: splitAt > 0 && splitAt < points.length - 1,
      splitAt,
      yFor: y,
      formatValue: makeAxisFormatter(niceMax),
      min: niceMin,
      max: niceMax,
    };
  }, [equity, liveFromBarTime, baseline, mode]);

  // Nearest sample to the cursor. Index-based rather than time-based because the x axis is
  // plotted by index (samples are evenly spaced on screen, not in time).
  const handleMove = (event) => {
    if (!geometry) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const index = Math.round(ratio * (geometry.points.length - 1));
    const point = geometry.points[index];
    if (!point) return;
    setHover({
      index,
      point,
      xPct: (index / (geometry.points.length - 1)) * 100,
      yPct: (geometry.yFor(point.v) / geometry.height) * 100,
      isLive: index >= geometry.splitAt,
    });
  };

  if (!geometry) {
    return <div className="signal-chart-empty">Not enough history yet — the curve appears once the first bars are processed.</div>;
  }

  return (
    <div className={`signal-chart-shell${mode === 'backtest' ? ' is-backtest' : ''}`}>
      {/* Canvas and money gutter are SIBLINGS, not a padded box.
          They used to be one element with padding-right, which quietly broke two things: an
          absolutely positioned child's `left: X%` resolves against the PADDING box, and
          getBoundingClientRect() returns the border box — both of which are wider than the SVG.
          So the crosshair and hover marker drifted right as the cursor approached the end of the
          curve and ended up sitting inside the scale, making it look as though the scale was
          covering the data. As siblings, every percentage and every measurement resolves against
          exactly the box the SVG occupies. */}
      <div className="signal-chart-row">
        <div
          className="signal-chart-canvas"
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
        <svg viewBox={`0 0 ${geometry.width} ${geometry.height}`} className="signal-chart" preserveAspectRatio="none">
          {/* Gridlines at the same values as the labels in the gutter. */}
          {geometry.valueTicks.map((tick) => (
            <line
              key={tick.value}
              x1="0" x2={geometry.width}
              y1={tick.ratio * geometry.height} y2={tick.ratio * geometry.height}
              className="signal-chart-grid"
            />
          ))}
          <line
            x1="0" x2={geometry.width} y1={geometry.baselineY} y2={geometry.baselineY}
            className="signal-chart-baseline"
          />
          {/* Balance (realised only) under equity (mark-to-market). The gap between them IS the
              open position's floating P&L, which is worth being able to see at a glance. */}
          {geometry.balancePath && <path d={geometry.balancePath} className="signal-chart-balance" />}
          {geometry.hasSplit && (
            <line
              x1={geometry.splitX} x2={geometry.splitX} y1="0" y2={geometry.height}
              className="signal-chart-split"
            />
          )}
          {/* Dashed = replayed backtest history that seeds the chart. Solid = proven forward. */}
          <path d={geometry.seedPath} className="signal-chart-seed" />
          {geometry.livePath && <path d={geometry.livePath} className="signal-chart-live" />}
        </svg>

        {/* Crosshair as HTML, not SVG: a 1px SVG line gets scaled horizontally by
            preserveAspectRatio="none" and stops being 1px. */}
        {hover && (
          <>
            <span className="signal-chart-crosshair" style={{ left: `${hover.xPct}%` }} />
            <span
              className={`signal-chart-dot ${hover.isLive ? 'live' : 'seed'}`}
              style={{ left: `${hover.xPct}%`, top: `${hover.yPct}%` }}
            />
          </>
        )}

        {hover && (
          <div
            className="signal-chart-tooltip"
            // Flips to the left of the cursor past the midpoint so it never runs off the edge.
            style={hover.xPct > 55
              ? { right: `${100 - hover.xPct}%`, marginRight: 12 }
              : { left: `${hover.xPct}%`, marginLeft: 12 }}
          >
            <strong>{formatBarTime(hover.point.t)}</strong>
            <span>
              Equity <b>{hover.point.v.toFixed(2)}</b>
            </span>
            {Number.isFinite(hover.point.b) && (
              <span>
                Balance <b>{hover.point.b.toFixed(2)}</b>
              </span>
            )}
            {Number.isFinite(hover.point.b) && Math.abs(hover.point.v - hover.point.b) > 0.005 && (
              <span className={hover.point.v - hover.point.b >= 0 ? 'up' : 'down'}>
                Open P&amp;L <b>{(hover.point.v - hover.point.b).toFixed(2)}</b>
              </span>
            )}
            <em>{hover.isLive ? 'Live' : 'Backtest history'}</em>
          </div>
        )}
        </div>

        {/* The money scale sits BESIDE the canvas, never over it — the live segment grows
            rightwards, so anything drawn on top of the plot would end up covering the most
            recent and most interesting part of the curve. */}
        <div className="signal-chart-yaxis" aria-hidden="true">
          {geometry.valueTicks.map((tick) => (
            <span key={tick.value} style={{ top: `${(tick.ratio * 100).toFixed(2)}%` }}>
              {geometry.formatValue(tick.value)}
            </span>
          ))}
        </div>
      </div>

      {/* The date labels get the same two-column treatment so they stay aligned with the curve
          rather than with the shell. */}
      <div className="signal-chart-axis-row">
        <div className="signal-chart-axis">
          {(geometry.ticks || []).map((tick) => (
            <span key={tick.time} style={{ left: `${(tick.ratio * 100).toFixed(2)}%` }}>{tick.label}</span>
          ))}
        </div>
        <div className="signal-chart-axis-gutter" aria-hidden="true" />
      </div>
      <div className="signal-chart-legend">
        {mode === 'backtest' ? (
          <span><i className="swatch backtest" /> Strategy backtest equity</span>
        ) : (
          <span><i className="swatch live" /> Live equity since deployment</span>
        )}
        <span><i className="swatch balance" /> Balance</span>
      </div>
    </div>
  );
}

// Profit by calendar period. Bars rather than a curve because the question is "which months
// made money", and a bar answers it without the reader having to trace a line.
function PeriodProfitChart({ trades, period, onPeriodChange }) {
  const buckets = useMemo(() => bucketTradesByPeriod(trades, period), [trades, period]);
  const peak = buckets.length ? Math.max(...buckets.map((b) => Math.abs(b.net)), 1) : 1;
  return (
    <section className="signal-panel">
      <div className="signal-panel-head">
        <h3>Profit by period</h3>
        <div className="signal-period-toggle">
          {['day', 'week', 'month'].map((option) => (
            <button
              key={option}
              type="button"
              className={period === option ? 'active' : ''}
              onClick={() => onPeriodChange(option)}
            >
              {option === 'day' ? 'Daily' : option === 'week' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>
      </div>
      {!buckets.length ? (
        <p className="muted-text">No live trades have closed yet.</p>
      ) : (
        <div className="signal-period-bars">
          {buckets.slice(-14).map((bucket) => (
            <div
              key={bucket.key}
              className="signal-period-bar"
              title={`${bucket.label}: ${formatSignalMoney(bucket.net)} over ${bucket.trades} trade${bucket.trades === 1 ? '' : 's'}`}
            >
              <div className="signal-period-track">
                <div
                  className={`signal-period-fill ${bucket.net >= 0 ? 'up' : 'down'}`}
                  style={{ height: `${Math.max(2, (Math.abs(bucket.net) / peak) * 100)}%` }}
                />
              </div>
              <span className="signal-period-label">{bucket.label}</span>
              <span className={`signal-period-value ${bucket.net >= 0 ? 'up' : 'down'}`}>
                {formatSignalMoney(bucket.net)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Creator side of the marketplace: turn a proven signal into something other people pay for.
//
// The proving bar (14 days live + 10 completed trades) is enforced on the server; this panel
// only explains where the signal stands, because "list" failing with an error after the fact
// is a worse experience than never offering it.
function ListingPanel({ deploymentId, listing, canList, rankingBar, liveTradeCount, daysLive, onChanged, confirm }) {
  const [headline, setHeadline] = useState(listing?.headline || '');
  const [description, setDescription] = useState(listing?.description || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isListed = listing && listing.status === 'active';

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await createListing(deploymentId, { headline, description });
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const delist = async () => {
    const confirmed = await confirm({
      title: 'Remove this listing?',
      body: 'It stops appearing in the marketplace. People already following it keep their '
        + 'alerts until they stop following.',
      confirmLabel: 'Remove listing',
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await removeListing(deploymentId);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!canList && !isListed) {
    const tradesShort = Math.max(0, rankingBar.trades - liveTradeCount);
    const daysShort = Math.max(0, rankingBar.days - daysLive);
    return (
      <section className="signal-panel">
        <h3>Share this signal</h3>
        <p className="muted-text">
          A signal has to prove itself before others can follow it: {rankingBar.days} days live
          and {rankingBar.trades} completed trades.
        </p>
        <ul className="signal-gate-list">
          <li className={daysShort ? '' : 'met'}>
            {daysLive}/{rankingBar.days} days live
            {daysShort ? ` — ${daysShort} to go` : ' ✓'}
          </li>
          <li className={tradesShort ? '' : 'met'}>
            {liveTradeCount}/{rankingBar.trades} live trades
            {tradesShort ? ` — ${tradesShort} to go` : ' ✓'}
          </li>
        </ul>
      </section>
    );
  }

  return (
    <section className="signal-panel signal-listing-panel">
      <h3>{isListed ? 'Your listing' : 'Share this signal'}</h3>

      {isListed && (
        <p className="signal-listing-live">
          Live in the marketplace · <strong>{listing.subscriberCount || 0}</strong> subscriber
          {listing.subscriberCount === 1 ? '' : 's'}
        </p>
      )}

      <label className="signal-field">
        <span>Headline</span>
        <input
          type="text"
          maxLength={120}
          value={headline}
          placeholder="What makes this signal worth following?"
          onChange={(event) => setHeadline(event.target.value)}
        />
      </label>

      <label className="signal-field">
        <span>Description</span>
        <textarea
          rows={4}
          maxLength={2000}
          value={description}
          placeholder="How it trades, when it sits out, what a bad week looks like."
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <p className="muted-text signal-split-note">
        Sharing is free. VixPremium members can follow your signal and copy it to their own
        MetaTrader — your strategy rules are never exposed, only the trades it takes.
      </p>

      {error && <p className="signal-error">{error}</p>}

      <div className="signal-listing-actions">
        <button type="button" className="primary-button compact-button" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : isListed ? 'Update listing' : 'Share in the marketplace'}
        </button>
        {isListed && (
          <button type="button" className="ghost-button compact-button" disabled={busy} onClick={delist}>
            Remove
          </button>
        )}
      </div>
    </section>
  );
}

// Subscriber side. Following is FREE and gated on VixPremium — creators are not paid, and the
// only thing anyone buys is the platform plan. A non-premium visitor still sees the full track
// record and can paper-follow, which is what makes the upgrade worth buying.
function SubscribePanel({ listing, subscription, creatorName, onChanged, confirm }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsPremium, setNeedsPremium] = useState(false);

  const subscribe = async () => {
    setBusy(true);
    setError('');
    try {
      await subscribeToListing(listing.id);
      await onChanged();
    } catch (err) {
      if (err.code === 'VIX_PREMIUM_REQUIRED') setNeedsPremium(true);
      else setError(err.message);
      setBusy(false);
    }
  };

  const cancel = async () => {
    const confirmed = await confirm({
      title: 'Stop following this signal?',
      body: 'You stop receiving its alerts and the copier stops trading it in MetaTrader. '
        + 'Positions already open are left alone — close those yourself. Your VixPremium plan '
        + 'is not affected.',
      confirmLabel: 'Stop following',
      tone: 'danger',
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await cancelSubscription(subscription.id);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (subscription && subscription.status === 'active') {
    return (
      <section className="signal-panel signal-subscribe-panel is-subscribed">
        <h3>You're following this</h3>
        <p className="muted-text">
          Every entry and exit reaches you the moment it fires, and the MT5 copier will trade it
          in your account if you have it running.
        </p>
        {error && <p className="signal-error">{error}</p>}
        <button type="button" className="ghost-button compact-button" disabled={busy} onClick={cancel}>
          {busy ? 'Working…' : 'Stop following'}
        </button>
      </section>
    );
  }

  return (
    <section className="signal-panel signal-subscribe-panel">
      <h3>Follow this signal</h3>
      {listing.headline && <p className="signal-listing-headline">{listing.headline}</p>}
      {listing.description && <p className="muted-text">{listing.description}</p>}

      <p className="signal-subscribe-price">Included<span> with VixPremium</span></p>

      <ul className="signal-subscribe-benefits">
        <li>Every entry and exit, in real time</li>
        <li>Telegram, email, Discord or in-app</li>
        <li>Copier EA trades it in your own MT5</li>
        <li>Stop any time</li>
      </ul>

      {needsPremium ? (
        <>
          <p className="signal-error">
            Following signals and copying them to MetaTrader needs VixPremium.
          </p>
          <button
            type="button"
            className="primary-button"
            onClick={() => { window.location.href = '/tester-premium'; }}
          >
            Get VixPremium
          </button>
        </>
      ) : (
        <>
          {error && <p className="signal-error">{error}</p>}
          <button type="button" className="primary-button" disabled={busy} onClick={subscribe}>
            {busy ? 'Working…' : 'Follow signal'}
          </button>
        </>
      )}
      <small className="muted-text">
        Free to follow with VixPremium. {creatorName || 'The creator'} shares this signal — you
        are copying their live results, not buying advice.
      </small>
    </section>
  );
}

function SignalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [profitPeriod, setProfitPeriod] = useState('month');
  const [confirm, confirmDialog] = useConfirm();
  const [returnNotice, setReturnNotice] = useState('');
  // Live by default: it is the record that actually belongs to this signal.
  const [chartView, setChartView] = useState('live');

  const load = useCallback(async () => {
    try {
      setData(await getDeployment(id));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Refresh while the page is open so an entry appears without a manual reload.
    const timer = setInterval(load, SIGNAL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    getNotificationPrefs(Number(id)).then(setPrefs).catch(() => setPrefs(null));
  }, [id]);

  // Coming back from PayPal's approval page. Activation happens on a WEBHOOK, not on this
  // redirect, so the subscription is very often still `pending` at this instant — saying
  // "subscribed!" here and showing an inactive panel would look broken. Poll briefly instead.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled')) {
      setReturnNotice('Checkout cancelled — nothing was charged.');
    } else if (params.get('subscribed')) {
      setReturnNotice('Payment approved. Activating your subscription…');
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        const fresh = await getDeployment(id).catch(() => null);
        if (fresh?.subscription?.status === 'active') {
          setData(fresh);
          setReturnNotice('You\'re subscribed. Alerts start with the next signal.');
          clearInterval(poll);
        } else if (attempts >= 10) {
          setReturnNotice(
            'Payment approved. PayPal hasn\'t confirmed it yet — this usually takes under a '
            + 'minute, and your access starts automatically once it does.',
          );
          clearInterval(poll);
        }
      }, 3000);
      return () => clearInterval(poll);
    }
    return undefined;
  }, [id]);

  const deployment = data?.deployment;
  // Memoized so the fallback [] is not a fresh array on every render, which would invalidate
  // every downstream useMemo that depends on it.
  const trades = useMemo(() => data?.trades || [], [data]);
  const stats = useMemo(() => summarizeSignalTrades(trades), [trades]);
  const drawdown = useMemo(() => maxDrawdownPct(data?.equity), [data]);
  const openPosition = trades.find((trade) => trade.status === 'open');

  // Only the portion since deployment is a real forward record; seed trades are replayed
  // history and are labelled as such rather than being quietly counted as live performance.
  const liveTrades = useMemo(
    () => trades.filter((trade) => Number(trade.entryTime) >= Number(deployment?.liveFromBarTime || 0)),
    [trades, deployment],
  );
  const liveStats = useMemo(() => summarizeSignalTrades(liveTrades), [liveTrades]);

  const lastEquityPoint = (data?.equity || []).length ? data.equity[data.equity.length - 1] : null;
  // Equity is mark-to-market, balance is realised only — the difference is exactly the open
  // position's unrealised profit.
  const floatingPnl = lastEquityPoint
    ? Number(lastEquityPoint.equity) - Number(lastEquityPoint.balance)
    : 0;

  // Balance at the moment of deployment — the line everything LIVE is measured from.
  //
  // The virtual account used to show the original deposit next to a balance that already
  // contained the seed replay's profit, which read as though the signal had made that money
  // live. A signal deployed today with zero live trades would show "$5,000 deposit, $11,917
  // balance". Everything live is measured from here instead, and the original deposit is shown
  // as context rather than as the baseline.
  const deployBalance = useMemo(() => {
    const points = data?.equity || [];
    const liveFrom = Number(deployment?.liveFromBarTime) || 0;
    if (!points.length) return Number(deployment?.virtualDeposit) || 0;
    // Last sample at or before the deployment boundary; falls back to the first sample for a
    // deployment with no seeded history at all.
    const atDeploy = [...points].reverse().find((point) => Number(point.barTime) <= liveFrom);
    const value = Number(atDeploy?.balance ?? points[0]?.balance);
    return Number.isFinite(value) ? value : (Number(deployment?.virtualDeposit) || 0);
  }, [data, deployment]);

  // Realised only, so it agrees with the live trade list. Floating P&L is shown separately.
  const livePnl = lastEquityPoint ? Number(lastEquityPoint.balance) - deployBalance : 0;

  const daysLive = deployment?.deployedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(deployment.deployedAt).getTime()) / 86400000))
    : 0;

  const togglePref = async (key, value) => {
    if (!prefs) return;
    setSaving(true);
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      // discordWebhookUrl is deliberately NOT sent: it is account-wide now, and echoing it
      // back per signal would copy it into this deployment's row and quietly fork it from the
      // account setting the user actually edits.
      await saveNotificationPrefs({
        deploymentId: Number(id),
        channels: next.channels,
        notifyOn: next.notifyOn,
        muted: next.muted,
      });
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  if (error) {
    return (
      <div className="signal-detail-page page-shell">
        <p className="signal-error">{error}</p>
        <button type="button" className="ghost-button compact-button" onClick={() => navigate('/dashboard?tab=signals')}>
          Back to signals
        </button>
      </div>
    );
  }

  if (!deployment) return <div className="signal-detail-page page-shell"><p className="muted-text">Loading…</p></div>;

  return (
    <HelmetProvider>
    <div className="signal-detail-page page-shell">
      <Helmet><title>{deployment.name} — Live Signal | Vix Algo Trading</title></Helmet>
      {confirmDialog}

      {returnNotice && (
        <p className="signal-return-notice" role="status">{returnNotice}</p>
      )}

      <header className="signal-detail-head">
        <div>
          <button type="button" className="signal-back" onClick={() => navigate('/dashboard?tab=signals')}>
            ← Signals
          </button>
          <h1>{deployment.name}</h1>
          <p className="muted-text">
            {deployment.symbol} · {deployment.timeframe} · live {daysLive} day{daysLive === 1 ? '' : 's'}
          </p>
        </div>
        <div className="signal-detail-actions">
          {/* The loop back into the product: every signal page is a route into the Builder. */}
          <button
            type="button"
            className="ghost-button compact-button"
            onClick={() => navigate(builderLink(deployment.strategyId, null, deployment))}
            disabled={!deployment.strategyId}
          >
            Refine in Builder
          </button>
          {data.isOwner && (
            <button
              type="button"
              className="ghost-button compact-button"
              onClick={async () => {
                await updateDeployment(id, {
                  status: deployment.status === 'running' ? 'paused' : 'running',
                });
                load();
              }}
            >
              {deployment.status === 'running' ? 'Pause' : 'Resume'}
            </button>
          )}
        </div>
      </header>

      {/* Verification badge. This is the claim no competitor can make: the track record was
          produced by the server executing a strategy that was locked at deploy time. */}
      <div className="signal-verify">
        <span className={`signal-pill ${data.meetsRankingBar ? 'ok' : ''}`}>
          {data.meetsRankingBar ? 'Verified' : 'Proving'}
        </span>
        <span className="muted-text">
          Strategy locked since {new Date(deployment.deployedAt).toLocaleDateString()} ·
          fingerprint <code>{String(deployment.snapshotHash || '').slice(0, 12)}</code>
          {!data.meetsRankingBar && data.rankingBar && (
            <> · needs {data.rankingBar.days} days and {data.rankingBar.trades} trades to be verified</>
          )}
        </span>
        {data.creator && !data.isOwner && (
          <button
            type="button"
            className="market-creator"
            onClick={() => navigate(`/signals/creators/${data.creator.id}`)}
          >
            by {data.creator.name}
          </button>
        )}
      </div>

      {(data.badges || []).length > 0 && (
        <div className="market-badges signal-badge-row">
          {data.badges.map((badge) => (
            <span key={badge.id} className="market-badge">{badge.label}</span>
          ))}
        </div>
      )}

      {/* Health: live results compared against the same strategy's behaviour over the bars
          just before it went live. When it drifts the panel's job is to route the user into
          the Builder, which is the whole reason drift tracking exists. */}
      {data.health && data.isOwner && (
        <div className={`signal-health signal-health-${data.health.status}`}>
          <div>
            <strong>
              {data.health.status === 'tracking' && 'Tracking its backtest'}
              {data.health.status === 'drifting' && 'Drifting from its backtest'}
              {data.health.status === 'underperforming' && 'Underperforming its backtest'}
              {data.health.status === 'warming_up' && 'Not enough live trades to assess yet'}
            </strong>
            <p className="muted-text">
              {data.health.status === 'warming_up'
                ? `${data.health.tradesUntilAssessed} more live trade${data.health.tradesUntilAssessed === 1 ? '' : 's'} needed before live results can be compared to the backtest.`
                : `Live average ${Number(data.health.liveExpectancy).toFixed(2)} per trade vs `
                  + `${Number(data.health.baselineExpectancy).toFixed(2)} in the backtest `
                  + `(${data.health.driftPct >= 0 ? '+' : ''}${Number(data.health.driftPct).toFixed(0)}%), `
                  + `over ${data.health.liveTrades} live trades.`}
            </p>
          </div>
          {(data.health.status === 'drifting' || data.health.status === 'underperforming') && (
            <button
              type="button"
              className="primary-cta compact-button"
              disabled={!deployment.strategyId}
              onClick={() => navigate(builderLink(deployment.strategyId, null, deployment))}
            >
              Review in Builder
            </button>
          )}
        </div>
      )}

      {/* Two accounts, two charts — never spliced together.
          LIVE is the signal's own record: it starts flat at the chosen deposit on the day of
          deployment and moves only on trades taken after it. BACKTEST is the strategy as saved
          in the Builder, on its own deposit, over as much history as the broker retains — and
          it keeps extending as the signal runs. Defaults to Live because that is the honest
          record; the backtest is context, one click away. */}
      <div className="signal-chart-switch" role="tablist" aria-label="Equity view">
        <button
          type="button"
          role="tab"
          aria-selected={chartView === 'live'}
          className={chartView === 'live' ? 'active' : ''}
          onClick={() => setChartView('live')}
        >
          Live performance
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={chartView === 'backtest'}
          className={chartView === 'backtest' ? 'active' : ''}
          onClick={() => setChartView('backtest')}
        >
          Strategy backtest
        </button>
      </div>

      {chartView === 'live' ? (
        <>
          <EquityChart
            equity={data.equity}
            liveFromBarTime={deployment.liveFromBarTime}
            baseline={Number(deployment.virtualDeposit) || deployBalance}
            mode="live"
          />
          <p className="signal-chart-note muted-text">
            Live account from {formatBarTime(deployment.liveFromBarTime)}, starting at{' '}
            ${Number(deployment.virtualDeposit || 0).toLocaleString()}. Only trades taken after
            deployment are included.
          </p>
        </>
      ) : (
        <>
          <EquityChart
            equity={data.backtestEquity || []}
            liveFromBarTime={0}
            baseline={data.backtestDeposit}
            mode="backtest"
          />
          <p className="signal-chart-note muted-text">
            The strategy as saved in the Builder, on its own{' '}
            ${Number(data.backtestDeposit || 0).toLocaleString()} deposit.
          </p>
        </>
      )}

      <section className="signal-stat-row">
        <div className="signal-stat">
          <span className="muted-text">Live trades</span>
          <strong>{liveStats.total}</strong>
          <small className="muted-text">{stats.total} incl. seed</small>
        </div>
        <div className="signal-stat">
          <span className="muted-text">Win rate</span>
          <strong>{liveStats.total ? `${liveStats.winRate.toFixed(0)}%` : '—'}</strong>
          <small className="muted-text">{liveStats.wins}W / {liveStats.losses}L</small>
        </div>
        <div className="signal-stat">
          <span className="muted-text">Net (live)</span>
          {/* The ACCOUNT's realised profit, the same number as Live P&L below — not the sum of the
              trade rows. They are two accounts of one figure and they were allowed to disagree:
              one signal reported +1503.49 of net profit on an account that had made +302.02,
              because the ledger held several copies of some trades and adding them up counted the
              same profit more than once. A balance curve cannot double-count; it is one value per
              bar. When the rows still disagree the difference is shown rather than hidden, because
              a ledger that has drifted is worth noticing. */}
          <strong className={livePnl >= 0 ? 'up' : 'down'}>
            {lastEquityPoint ? formatSignalMoney(livePnl) : '—'}
          </strong>
          {lastEquityPoint && Math.abs(livePnl - liveStats.netProfit) >= 0.01 && (
            <small className="muted-text">
              trade rows total {formatSignalMoney(liveStats.netProfit)}
            </small>
          )}
        </div>
        <div className="signal-stat">
          <span className="muted-text">Profit factor</span>
          {/* With no closed live trades the computed factor is 0, which reads as "terrible"
              rather than "no data yet" — so show a dash until there is something to measure. */}
          <strong>
            {liveStats.total && Number.isFinite(liveStats.profitFactor)
              ? liveStats.profitFactor.toFixed(2)
              : '—'}
          </strong>
        </div>
        <div className="signal-stat">
          <span className="muted-text">Avg R</span>
          <strong>{liveStats.avgR === null ? '—' : `${liveStats.avgR >= 0 ? '+' : ''}${liveStats.avgR.toFixed(2)}`}</strong>
        </div>
        <div className="signal-stat">
          <span className="muted-text">Max drawdown</span>
          <strong>{drawdown.toFixed(1)}%</strong>
        </div>
      </section>

      {openPosition && (
        <section className="signal-open-panel">
          <h3>
            <span className={`signal-dot ${openPosition.side === 'BUY' ? 'up' : 'down'}`} />
            Open {openPosition.side === 'BUY' ? 'long' : 'short'}
          </h3>
          <dl>
            <div><dt>Entry</dt><dd>{Number(openPosition.entryPrice).toFixed(2)}</dd></div>
            <div><dt>Stop</dt><dd>{openPosition.stopPrice ? Number(openPosition.stopPrice).toFixed(2) : '—'}</dd></div>
            <div><dt>Target</dt><dd>{openPosition.targetPrice ? Number(openPosition.targetPrice).toFixed(2) : '—'}</dd></div>
            <div><dt>Opened</dt><dd>{formatBarTime(openPosition.entryTime)}</dd></div>
          </dl>
        </section>
      )}

      {/* The cloud demo account behind the signal, stated plainly — without this the equity
          curve is a shape with no units attached to it. */}
      <section className="signal-account-panel">
        <h3>Virtual account</h3>
        <dl>
          <div>
            {/* The account now opens at the chosen deposit — the seed replay's profit and loss
                is shifted out, so this reads back the number the user actually entered. */}
            <dt>Starting deposit</dt>
            <dd>${Number(deployment.virtualDeposit || deployBalance).toFixed(2)}</dd>
          </div>
          <div>
            <dt>Balance</dt>
            <dd>{lastEquityPoint ? `$${Number(lastEquityPoint.balance).toFixed(2)}` : '—'}</dd>
          </div>
          <div>
            <dt>Equity</dt>
            <dd>{lastEquityPoint ? `$${Number(lastEquityPoint.equity).toFixed(2)}` : '—'}</dd>
          </div>
          <div>
            <dt>Live P&amp;L</dt>
            <dd className={livePnl > 0 ? 'up' : livePnl < 0 ? 'down' : ''}>
              {lastEquityPoint ? formatSignalMoney(livePnl) : '—'}
            </dd>
          </div>
          <div>
            <dt>Floating P&amp;L</dt>
            <dd className={floatingPnl >= 0 ? 'up' : 'down'}>
              {lastEquityPoint ? formatSignalMoney(floatingPnl) : '—'}
            </dd>
          </div>
          <div>
            <dt>Market</dt>
            <dd>{deployment.symbol} {deployment.timeframe}</dd>
          </div>
          <div>
            <dt>Evaluated to</dt>
            <dd>{formatBarTime(deployment.lastEvaluatedBarTime)}</dd>
          </div>
        </dl>
      </section>

      <PeriodProfitChart
        trades={liveTrades}
        period={profitPeriod}
        onPeriodChange={setProfitPeriod}
      />

      <div className="signal-detail-grid">
        <section className="signal-panel">
          <h3>Trades</h3>
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Side</th><th>Entry</th><th>Exit</th><th>Result</th><th>R</th><th>Reason</th><th />
                </tr>
              </thead>
              <tbody>
                {trades.length === 0 ? (
                  <tr><td colSpan="7" className="empty-cell">No trades yet.</td></tr>
                ) : trades.slice(0, 60).map((trade) => {
                  const isSeed = Number(trade.entryTime) < Number(deployment.liveFromBarTime || 0);
                  return (
                    <tr key={trade.tradeKey} className={isSeed ? 'signal-seed-row' : ''}>
                      <td>
                        <span className={`signal-side ${trade.side === 'BUY' ? 'up' : 'down'}`}>{trade.side}</span>
                        {isSeed && <span className="signal-seed-tag">seed</span>}
                      </td>
                      <td>
                        {Number(trade.entryPrice).toFixed(2)}
                        <small className="muted-text">{formatBarTime(trade.entryTime)}</small>
                      </td>
                      <td>
                        {trade.exitPrice ? Number(trade.exitPrice).toFixed(2) : <em>open</em>}
                        {trade.exitTime ? <small className="muted-text">{formatBarTime(trade.exitTime)}</small> : null}
                      </td>
                      <td className={Number(trade.pnl) >= 0 ? 'up' : 'down'}>
                        {trade.pnl === null ? '—' : formatSignalMoney(trade.pnl)}
                      </td>
                      <td>{Number.isFinite(Number(trade.rMultiple)) ? Number(trade.rMultiple).toFixed(2) : '—'}</td>
                      <td className="muted-text">{trade.closeReason || '—'}</td>
                      <td>
                        {/* Opens the Builder at this trade so the user can watch it play out
                            on the chart — the same visualization they already know. */}
                        <button
                          type="button"
                          className="ghost-button compact-button"
                          disabled={!deployment.strategyId}
                          onClick={() => navigate(builderLink(deployment.strategyId, trade, deployment))}
                        >
                          Replay
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="signal-side-column">
          {/* Paid access sits above the free practice account: someone who has decided to
              subscribe should not have to scroll past the free option to do it. */}
          {!data.isOwner && data.listing && data.listing.status === 'active' && (
            <SubscribePanel
              listing={data.listing}
              subscription={data.subscription}
              creatorName={data.creator?.name}
              onChanged={load}
              confirm={confirm}
            />
          )}

          {data.isOwner && (
            <ListingPanel
              deploymentId={Number(id)}
              listing={data.listing}
              canList={data.canList}
              rankingBar={data.rankingBar || { days: 14, trades: 10 }}
              liveTradeCount={liveStats.total}
              daysLive={daysLive}
              onChanged={load}
              confirm={confirm}
            />
          )}

          {/* Following your own signal makes no sense, so the panel is for other viewers. */}
          {!data.isOwner && (
            <PaperFollowPanel
              deploymentId={Number(id)}
              follow={data.follow}
              ledger={data.paperLedger}
              onChanged={load}
            />
          )}

          <section className="signal-panel">
            <h3>Activity</h3>
            <ul className="signal-event-list">
              {(data.events || []).length === 0 ? (
                <li className="muted-text">Nothing yet.</li>
              ) : data.events.slice(0, 12).map((event) => (
                <li key={event.id}>
                  <strong>{EVENT_LABELS[event.type] || event.type}</strong>
                  <span className="muted-text">{new Date(event.createdAt).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </section>

          {data.isOwner && prefs && (
            <section className="signal-panel">
              <h3>Alerts {saving && <small className="muted-text">saving…</small>}</h3>
              <div className="signal-toggle-list">
                {Object.entries(CHANNEL_LABELS).map(([channel, label]) => (
                  <label key={channel} className="signal-toggle">
                    <input
                      type="checkbox"
                      checked={(prefs.channels || []).includes(channel)}
                      onChange={() => togglePref(
                        'channels',
                        (prefs.channels || []).includes(channel)
                          ? prefs.channels.filter((item) => item !== channel)
                          : [...(prefs.channels || []), channel],
                      )}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="signal-toggle-list">
                {NOTIFY_OPTIONS.map((type) => (
                  <label key={type} className="signal-toggle">
                    <input
                      type="checkbox"
                      checked={(prefs.notifyOn || []).includes(type)}
                      onChange={() => togglePref(
                        'notifyOn',
                        (prefs.notifyOn || []).includes(type)
                          ? prefs.notifyOn.filter((item) => item !== type)
                          : [...(prefs.notifyOn || []), type],
                      )}
                    />
                    <span>{EVENT_LABELS[type]}</span>
                  </label>
                ))}
              </div>

              {/* Contact details (Discord webhook, Telegram, email) are account-wide and live
                  in Alert settings on the dashboard. Asking for them again per signal is what
                  made the setup-time test button useless. */}
              <p className="muted-text signal-alerts-hint">
                Channels and destinations are set once under{' '}
                <a href="/dashboard?tab=signals">Alert settings</a>.
              </p>

            </section>
          )}

          {data.isOwner && (
            <section className="signal-panel">
              <h3>Visibility</h3>
              <select
                value={deployment.visibility}
                onChange={async (event) => {
                  await updateDeployment(id, { visibility: event.target.value });
                  load();
                }}
              >
                <option value="private">Private — only you</option>
                <option value="unlisted">Unlisted — anyone with the link</option>
                <option value="public">Public — listed for other traders</option>
              </select>
              <p className="muted-text">
                Public signals show the track record and stats. Your strategy rules are never
                exposed.
              </p>
            </section>
          )}
        </aside>
      </div>
    </div>
    </HelmetProvider>
  );
}

export default SignalDetailPage;
